/**
 * Cache-bust query param for Shopify Data app proxy calls (/apps/shopifyData/*).
 * Bump SHOPIFY_DATA_APP_VERSION after backend/proxy migrations so browsers
 * treat endpoint URLs as new and skip stale HTTP caches.
 *
 * General stores (settings.cstack_store_type === 'general') do not install the
 * Shopify Data app. Those proxy calls are answered locally so the storefront
 * does not request a missing app and receive 404s.
 *
 * Load this script early (no defer) so it wraps fetch before other theme scripts run.
 */
(function (window) {
  'use strict';

  // Auto version by local date (YYYYMMDD-cm-app) so proxy URLs rotate daily and skip stale caches.
  var now = new Date();
  var yyyy = String(now.getFullYear());
  var mm = String(now.getMonth() + 1).padStart(2, '0');
  var dd = String(now.getDate()).padStart(2, '0');
  var SHOPIFY_DATA_APP_VERSION = yyyy + mm + dd + '-cm-app';
  var PROXY_MARKER = '/apps/shopifyData';

  function requestUrl(input) {
    if (typeof input === 'string') return input;
    if (typeof URL !== 'undefined' && input instanceof URL) return input.href;
    if (typeof Request !== 'undefined' && input instanceof Request) return input.url;
    return '';
  }

  function isShopifyDataProxyUrl(url) {
    return typeof url === 'string' && url.indexOf(PROXY_MARKER) !== -1;
  }

  function isGeneralStore() {
    return String(window.CSTACK_STORE_TYPE || '').toLowerCase().trim() === 'general';
  }

  /**
   * Empty payload for callers that already tolerate "no textbook data".
   * Metafield and session routes stay unsuccessful so fee lines and session
   * tokens are not created from a missing app.
   * @param {string} url
   * @returns {Response}
   */
  function skippedShopifyDataResponse(url) {
    var path = String(url || '');
    var denySuccess =
      path.indexOf('/getMetafields') !== -1 || path.indexOf('/session/') !== -1;
    var body = {
      success: !denySuccess,
      status: false,
      data: [],
      products: [],
      skipped: true,
    };
    return new Response(JSON.stringify(body), {
      status: 200,
      headers: { 'Content-Type': 'application/json' },
    });
  }

  function withShopifyDataVersion(url) {
    if (!isShopifyDataProxyUrl(url)) {
      return url;
    }
    if (/[?&]v=/.test(url)) {
      return url;
    }
    var sep = url.indexOf('?') >= 0 ? '&' : '?';
    return url + sep + 'v=' + encodeURIComponent(SHOPIFY_DATA_APP_VERSION);
  }

  window.SHOPIFY_DATA_APP_VERSION = SHOPIFY_DATA_APP_VERSION;
  window.withShopifyDataVersion = withShopifyDataVersion;
  window.shopifyDataProxyEnabled = !isGeneralStore();

  var originalFetch = window.fetch;
  if (typeof originalFetch !== 'function') {
    return;
  }

  window.fetch = function (input, init) {
    var url = '';
    try {
      url = requestUrl(input);
      if (isGeneralStore() && isShopifyDataProxyUrl(url)) {
        return Promise.resolve(skippedShopifyDataResponse(url));
      }      
      if (typeof input === 'string') {
        input = withShopifyDataVersion(input);
      } else if (typeof URL !== 'undefined' && input instanceof URL) {
        var versionedHref = withShopifyDataVersion(input.href);
        if (versionedHref !== input.href) {
          input = new URL(versionedHref);
        }
      } else if (typeof Request !== 'undefined' && input instanceof Request) {
        var versionedRequestUrl = withShopifyDataVersion(input.url);
        if (versionedRequestUrl !== input.url) {
          input = new Request(versionedRequestUrl, input);
        }
      }
    } catch (e) {
      // Fall through to original fetch if URL rewriting fails.
    }

    return originalFetch.call(this, input, init);
  };
})(window);
